import React, { useState } from 'react'
import { useUser } from 'util/UserContext'
import { Info, Loader2 } from 'lucide-react'
import Modal from 'ui/Modal'
import InputTextArea from 'ui/InputTextArea'
import { updateUserBio } from 'util/userUtils'
import ActorPrivacy from 'components/Common/ActorPrivacy'

interface Props {
    onClose: () => void
}

const BRIEF_REQUIREMENTS = {
    maxLength: 300
};

const UserAboutBriefModal: React.FC<Props> = ({ onClose }) => {
    const { currentUser, refreshUser } = useUser()
    const [brief, setBrief] = useState<string>(currentUser?.properties?.about_bio_brief?.value || '');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleBriefChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setBrief(e.target.value)
        setError(null)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null);
        setIsSubmitting(true);
        try {
            await updateUserBio(brief)
            await refreshUser();
            onClose()
        } catch (error) {
            console.error('Error updating brief:', error)
            setError('Failed to update brief. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = (): void => {
        onClose()
    }

    const isButtonDisabled = isSubmitting;

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            <button
                type="button"
                onClick={onClose}
                className="btn-secondary w-full sm:w-fit py-2 px-4"
            >
                Cancel
            </button>
            <button
                type="submit"
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                className={`w-full sm:w-fit py-2 px-4 ${
                    isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
            </button>
        </div>
    );

    return (
        <Modal isOpen={true} onClose={handleClose} title="About you" footer={footer} stickyFooter>
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Tell others about yourself. This will be displayed on your profile based on your privacy settings.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control space-y-4">
                            <div className="relative">
                                <InputTextArea
                                    id="brief"
                                    name="brief"
                                    labelText="Brief"
                                    fieldPlaceholder="About you"
                                    value={brief}
                                    onChange={handleBriefChange}
                                    spellCheck={true}
                                    maxLength={BRIEF_REQUIREMENTS.maxLength}
                                />
                            </div>

                        <div className="relative">
                            <ActorPrivacy 
                                privacy={'public'}
                                settingKey="about_bio_brief"
                                onPrivacyChange={() => {}}
                                actorType="user"
                                editMode={false}
                                showDropdown={false}
                                miniIcon={false}
                                disabled={true}
                                className="opacity-50"
                            />
                        </div>

                            {error && (
                                <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                    <Info className="mr-1 size-4" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                {/* Action buttons moved to Modal footer */}
            </div>
        </Modal>
    );
};

export default UserAboutBriefModal; 